/**
 * StrategiesListView Component
 *
 * Displays a list of strategies in a table format with real-time updates.
 *
 * Features:
 * - FR-001: Display all strategies in table format
 * - FR-002: Show name, type, creation date, last updated date
 * - FR-003: Real-time updates when strategies are created, updated, or deleted
 * - Action buttons: Edit (Sprint 9), Clone (Sprint 10), Delete
 *
 * Related: Sprint 8, Tasks T036.1, T036.2, T036.3
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Typography, useTheme, Stack, Menu, MenuItem, Button, Snackbar, Alert, Chip, Tooltip } from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import { WithGuideHelp } from '@modules/ui-components/dialogs/WithGuideHelp';
import {
  SDataTable,
  type SDataTableColumnDef,
  type SDataTableRowSelectionState,
} from '@modules/ui-components/dialogs/SDataTable';
import type { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import type { Strategy } from '@segmently/schema-models/strategy';
import { buildStrategyCanvasUrl } from '@modules/navigation/constants/routes';

export interface StrategiesListViewProps {
  /** Project ID for which to display strategies */
  projectId: string;

  /** Strategy service instance (dependency injection for testing) */
  strategyService: StrategyService;

  /** Optional: Called when Create button is clicked (Sprint 6) */
  onCreate?: () => void;

  /** Optional: Called when Edit button is clicked (Sprint 9) */
  onEdit?: (strategy: Strategy) => void;

  /** Optional: Called when Clone button is clicked (Sprint 10) */
  onClone?: (strategy: Strategy) => void;

  /** Optional: Called when Delete button is clicked */
  onDelete?: (strategy: Strategy) => void;

  /**
   * Optional: called with the number of strategies whenever the list state
   * settles (initial load and every real-time update). The project page keeps
   * the count so the create dialog can decide whether this is the project's
   * FIRST strategy (W12 Part B, Task B1).
   */
  onStrategiesLoaded?: (count: number) => void;
}

/**
 * Format ISO date string to two-line format (FR-009)
 * Line 1: MMM DD, YYYY
 * Line 2: HH:mm
 */
const formatDate = (isoString?: string): React.ReactNode => {
  if (!isoString) return 'N/A';

  try {
    const date = new Date(isoString);

    // Format date line: Oct 29, 2025
    const dateLine = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

    // Format time line: 14:30 (use UTC to match test expectations)
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    const timeLine = `${hours}:${minutes}`;

    return (
      <Stack spacing={0}>
        <Typography variant="body2">{dateLine}</Typography>
        <Typography variant="body2" color="text.secondary">{timeLine}</Typography>
      </Stack>
    );
  } catch (error) {
    return 'Invalid date';
  }
};

/**
 * Truncate description to 500 characters with ellipsis (FR-001)
 */
const truncateDescription = (description?: string): string => {
  if (!description) return '';
  if (description.length <= 500) return description;
  return description.substring(0, 500) + '...';
};

export const StrategiesListView: React.FC<StrategiesListViewProps> = ({
  projectId,
  strategyService,
  onCreate,
  onEdit,
  onClone,
  onDelete,
  onStrategiesLoaded,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [rowSelection, setRowSelection] = useState<SDataTableRowSelectionState>({});

  // Initialize guide service for help system
  const guideService = React.useMemo(() => new GuideService(), []);

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );

  const selectedStrategies = useMemo(
    () => strategies.filter((strategy) => strategy.id && selectedIds.includes(strategy.id)),
    [strategies, selectedIds],
  );

  // Actions menu state (FR-003)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null);
  const menuOpen = Boolean(anchorEl);

  // Delete confirmation dialog state (FR-005)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [strategiesToDelete, setStrategiesToDelete] = useState<Strategy[]>([]);

  // Toast notification state (FR-006)
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Show toast notification (FR-006)
  const showToast = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Close toast notification
  const handleCloseSnackbar = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  // Open actions menu (FR-003)
  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>, strategy: Strategy) => {
    event.stopPropagation(); // Prevent row click
    setAnchorEl(event.currentTarget);
    setSelectedStrategy(strategy);
  }, []);

  // Close actions menu
  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setSelectedStrategy(null);
  }, []);

  // Handle Edit menu item click
  const handleEditClick = useCallback(() => {
    if (!selectedStrategy) return;

    handleCloseMenu();

    if (onEdit) {
      onEdit(selectedStrategy);
    } else {
      console.log('[StrategiesListView] Edit clicked (Sprint 9):', selectedStrategy.id);
      // TODO Sprint 9: Open StrategyFormDialog in edit mode
    }
  }, [selectedStrategy, onEdit, handleCloseMenu]);

  // Handle Edit button click (legacy - will be removed)
  const handleEdit = useCallback((strategy: Strategy) => {
    if (onEdit) {
      onEdit(strategy);
    } else {
      console.log('[StrategiesListView] Edit clicked (Sprint 9):', strategy.id);
      // TODO Sprint 9: Open StrategyFormDialog in edit mode
    }
  }, [onEdit]);

  // Handle Clone menu item click
  const handleCloneClick = useCallback(async () => {
    if (!selectedStrategy) return;

    handleCloseMenu();

    console.log('[StrategiesListView] Clone button clicked, strategy:', selectedStrategy.id);

    if (onClone) {
      console.log('[StrategiesListView] Using external onClone callback');
      onClone(selectedStrategy);
      return;
    }

    // Guard: check strategyService exists
    if (!strategyService) {
      console.error('[StrategiesListView] strategyService is undefined!');
      alert('Error: Strategy service not available');
      return;
    }

    // Guard: check strategy has ID
    if (!selectedStrategy.id) {
      console.error('[StrategiesListView] Strategy has no ID:', selectedStrategy);
      alert('Error: Strategy ID is missing');
      return;
    }

    console.log('[StrategiesListView] Starting clone operation...');

    try {
      const cloned = await strategyService.cloneStrategy(selectedStrategy.id);
      console.log('[StrategiesListView] ✅ Clone completed:', cloned.id);
      console.log('[StrategiesListView] Cloned name:', cloned.name);

      // Show success toast (FR-006)
      showToast('Strategy cloned successfully', 'success');

      // Real-time listener will automatically update the list
    } catch (error: any) {
      console.error('[StrategiesListView] ❌ Clone failed:', error);
      console.error('[StrategiesListView] Error message:', error?.message);
      console.error('[StrategiesListView] Error stack:', error?.stack);

      // Show error toast (FR-006)
      showToast(`Failed to clone strategy: ${error?.message || 'Unknown error'}`, 'error');
    }
  }, [selectedStrategy, onClone, strategyService, handleCloseMenu, showToast]);

  // Handle Create button click (Sprint 6)
  const handleCreateClick = useCallback(() => {
    if (onCreate) {
      onCreate();
    }
  }, [onCreate]);

  // Handle Delete menu item click (FR-005)
  const handleDeleteClick = useCallback(() => {
    if (!selectedStrategy) return;

    handleCloseMenu();

    if (onDelete) {
      onDelete(selectedStrategy);
      return;
    }

    // Show BaseDialog confirmation
    setStrategiesToDelete([selectedStrategy]);
    setDeleteDialogOpen(true);
  }, [selectedStrategy, onDelete, handleCloseMenu]);

  const handleBulkDeleteClick = useCallback(() => {
    if (selectedStrategies.length === 0) return;
    setStrategiesToDelete(selectedStrategies);
    setDeleteDialogOpen(true);
  }, [selectedStrategies]);

  // Handle Open Canvas menu item click — navigates to dedicated URL
  const handleOpenCanvas = useCallback(() => {
    if (!selectedStrategy?.id) return;
    handleCloseMenu();
    navigate(buildStrategyCanvasUrl(projectId, selectedStrategy.id));
  }, [selectedStrategy, projectId, navigate, handleCloseMenu]);

  // Handle Copy Link — copies shareable URL to clipboard
  const handleCopyLink = useCallback(async (strategy: Strategy) => {
    if (!strategy.id) return;
    const url = `${window.location.origin}${buildStrategyCanvasUrl(projectId, strategy.id)}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('Link copied to clipboard', 'success');
    } catch {
      showToast('Failed to copy link', 'error');
    }
  }, [projectId, showToast]);

  // Confirm delete in dialog
  const handleConfirmDelete = useCallback(async () => {
    if (strategiesToDelete.length === 0) return;

    setDeleteDialogOpen(false);

    try {
      console.log('[StrategiesListView] Deleting strategies:', strategiesToDelete.map((strategy) => strategy.id));
      await strategyService.deleteMany(strategiesToDelete.map((strategy) => strategy.id!).filter(Boolean));
      console.log('[StrategiesListView] Strategies deleted successfully');

      // Show success toast (FR-006)
      showToast(strategiesToDelete.length === 1 ? 'Strategy deleted successfully' : `${strategiesToDelete.length} strategies deleted successfully`, 'success');
      setRowSelection({});

      // No need to update state - real-time listener will handle it
    } catch (error: any) {
      console.error('[StrategiesListView] Delete failed:', error);

      // Show error toast (FR-006)
      showToast(`Failed to delete strategy: ${error?.message || 'Please try again'}`, 'error');
    } finally {
      setStrategiesToDelete([]);
    }
  }, [strategiesToDelete, strategyService, showToast]);

  // Cancel delete in dialog
  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setStrategiesToDelete([]);
  }, []);


  // Define table columns
  const columns = useMemo<SDataTableColumnDef<Strategy>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 220,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      enableSorting: false,
      size: 360,
      Cell: ({ cell }) => truncateDescription(cell.getValue<string>()),
      Header: () => (
        <WithGuideHelp
          guideKey="strategies-description-help"
          guideService={guideService}
          position="top-right"
          iconSize="small"
        >
          <span>Description</span>
        </WithGuideHelp>
      ),
    },
    {
      accessorKey: 'strategyType',
      header: 'Strategy Type',
      size: 170,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      size: 180,
      Cell: ({ cell }) => formatDate(cell.getValue<string>()),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 130,
      enableSorting: false,
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0} justifyContent="center">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleCopyLink(row.original); }}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.primary.main,
              },
            }}
            aria-label="Copy strategy link"
            title="Copy link"
            data-testid={`strategy-copy-link-${row.original.id}`}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); window.open(buildStrategyCanvasUrl(projectId, row.original.id!), '_blank'); }}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.primary.main,
              },
            }}
            aria-label="Open canvas in new tab"
            title="Open in new tab"
            data-testid={`strategy-open-tab-${row.original.id}`}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => handleOpenMenu(e, row.original)}
            sx={{
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            aria-label="Open actions menu"
            title="Actions"
            data-testid={`strategy-actions-btn-${row.original.id}`}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ], [guideService, handleCopyLink, handleOpenMenu, projectId, theme.palette.action.hover, theme.palette.primary.main, theme.palette.text.primary, theme.palette.text.secondary]);

  // Initial load: Fetch strategies from Firestore
  useEffect(() => {
    let isMounted = true;

    const loadStrategies = async () => {
      try {
        setLoading(true);
        const strategies = await strategyService.list({ limit: null });

        if (isMounted) {
          setStrategies(strategies);
          setLoading(false);
        }
      } catch (error) {
        console.error('[StrategiesListView] Failed to load strategies:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadStrategies();

    return () => {
      isMounted = false;
    };
  }, [strategyService]);

  // Real-time subscription: Listen to collection changes using StrategyService (T036.2)
  useEffect(() => {
    console.log('[StrategiesListView] Setting up real-time listener via StrategyService');

    const unsubscribe = strategyService.onCollectionSnapshot(
      (strategies) => {
        console.log('[StrategiesListView] Real-time update received:', strategies.length, 'strategies');
        setStrategies(strategies);
        setLoading(false);
      },
      (error) => {
        console.error('[StrategiesListView] Real-time listener error:', error);
        setLoading(false);
      },
      { limit: null, orderByField: 'createdAt', orderDirection: 'desc' }
    );

    console.log('[StrategiesListView] Real-time listener setup complete');

    // Cleanup: Unsubscribe on unmount
    return () => {
      console.log('[StrategiesListView] Unsubscribing from real-time updates');
      unsubscribe();
    };
  }, [strategyService]);

  // Report the settled count upward (never mid-load — a transient 0 would make
  // the create dialog claim "your first strategy" on a populated project).
  useEffect(() => {
    if (loading) return;
    onStrategiesLoaded?.(strategies.length);
  }, [loading, strategies.length, onStrategiesLoaded]);

  // Empty state
  if (!loading && strategies.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight={400}
        gap={2}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(4),
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No strategies found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first strategy to get started
        </Typography>
        <WithGuideHelp
          guideKey="create-strategy-help"
          guideService={guideService}
          position="top-right"
          iconSize="small"
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
            sx={{ textTransform: 'none', mt: 2 }}
            data-testid="strategies-create-btn"
          >
            Create Strategy
          </Button>
        </WithGuideHelp>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 220px)', minHeight: 420, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedIds.length > 0 && (
            <>
              <Chip
                label={selectedIds.length}
                size="small"
                color="primary"
                sx={{ fontWeight: 'fontWeightMedium' }}
                data-testid="strategies-selected-count"
              />
              <Tooltip title="Delete selected">
                <IconButton
                  onClick={handleBulkDeleteClick}
                  color="error"
                  size="small"
                  aria-label="Delete selected strategies"
                  data-testid="strategies-bulk-delete"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                onClick={() => setRowSelection({})}
                sx={{ textTransform: 'none' }}
                data-testid="strategies-clear-selection"
              >
                Clear selection
              </Button>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <WithGuideHelp
            guideKey="create-strategy-help"
            guideService={guideService}
            position="top-right"
            iconSize="small"
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateClick}
              startIcon={<AddIcon />}
              sx={{ textTransform: 'none' }}
              data-testid="strategies-create-btn"
            >
              Create Strategy
            </Button>
          </WithGuideHelp>
        </Box>
      </Box>

      <SDataTable<Strategy>
        data={strategies}
        columns={columns}
        getRowId={(row) => row.id || ''}
        enableRowSelection
        enableSorting
        enablePagination={false}
        enableStickyHeader
        enableTopToolbar={false}
        state={{ rowSelection, isLoading: loading }}
        onRowSelectionChange={setRowSelection}
        muiSelectCheckboxProps={{ size: 'small', color: 'primary' }}
        muiSelectAllCheckboxProps={{ size: 'small' }}
        muiTablePaperProps={{
          variant: 'outlined',
          elevation: 0,
          'data-testid': 'strategies-list-table',
          sx: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
        } as any}
        muiTableContainerProps={{
          sx: { flex: 1, minHeight: 0, maxHeight: 'none', overflow: 'auto' },
        }}
        muiTableBodyRowProps={({ row }) => ({
          hover: true,
          onClick: () => {
            if (selectedIds.length > 0) {
              row.toggleSelected();
              return;
            }
            handleEdit(row.original);
          },
          sx: {
            cursor: 'pointer',
          }
        })}
      />

      {/* Actions dropdown menu (FR-003) */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditClick} data-testid="strategy-menu-edit">
          <Typography variant="body2">Edit</Typography>
        </MenuItem>
        <MenuItem onClick={handleCloneClick} data-testid="strategy-menu-clone">
          <Typography variant="body2">Clone</Typography>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} data-testid="strategy-menu-delete">
          <Typography variant="body2">Delete</Typography>
        </MenuItem>
        <MenuItem onClick={handleOpenCanvas} data-testid="strategy-menu-open-canvas">
          <Typography variant="body2">Open Canvas</Typography>
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog (FR-005) */}
      <SDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        title={strategiesToDelete.length > 1 ? 'Delete Strategies' : 'Delete Strategy'}
        primaryAction={{
          label: 'Delete',
          onClick: handleConfirmDelete,
          color: 'error',
          'aria-label': 'Confirm delete strategy',
          'data-testid': 'strategies-delete-confirm',
        }}
      >
        <Typography variant="body1">
          {strategiesToDelete.length > 1 ? (
            `Are you sure you want to delete ${strategiesToDelete.length} strategies?`
          ) : (
            <>
              Are you sure you want to delete &quot;
              <strong>{strategiesToDelete[0]?.name || 'this strategy'}</strong>
              &quot;?
            </>
          )}
        </Typography>
        {strategiesToDelete.length > 1 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {strategiesToDelete.slice(0, 4).map((strategy) => strategy.name || strategy.id).join(', ')}
            {strategiesToDelete.length > 4 ? `, and ${strategiesToDelete.length - 4} more` : ''}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This will also delete all blocks and screens in the selected strategy data. This action cannot be undone.
        </Typography>
      </SDialog>

      {/* Toast notifications (FR-006) */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </Box>
  );
};

StrategiesListView.displayName = 'StrategiesListView';
